// Wraps calls to pomps-auth-api, the Azure AD-backed login broker shared
// across Pomp's internal apps. See its README for full API documentation.
//
// Note: /api/auth/callback is a POST, but reads `code` (and `app`) from the
// query string, not the request body -- the README's "Architecture" section
// says GET, but the actual router registers it as POST (confirmed against
// internal/router/router.go and internal/handler/callback.go). This matches
// the real implementation, not the inconsistent doc section.

const authApiUrl = import.meta.env.VITE_AUTH_URL as string
const ersAppId = import.meta.env.VITE_APP_GUID as string

export interface AuthTokens {
    access_token: string
    refresh_token: string
}

interface AuthErrorBody {
    code?: string
    message?: string
}

async function parseAuthError(response: Response, fallback: string): Promise<Error> {
    const body = await response.json().catch(() => null) as AuthErrorBody | null
    return new Error(body?.message ?? fallback)
}

export const authService = {
    /**
     * Returns the Azure AD OAuth authorization URL to redirect the browser to.
     * The Referer header pomps-auth-api uses to pick the right redirect URI is
     * set automatically by the browser -- it can't and shouldn't be set manually.
     */
    async getLoginUrl(): Promise<string> {
        const response = await fetch(`${authApiUrl}/api/auth/login?app=${ersAppId}`)
        if (!response.ok) throw await parseAuthError(response, 'Failed to start login')
        const data = await response.json() as { url: string }
        return data.url
    },

    /** Exchanges the Azure AD authorization code for our access/refresh tokens. */
    async exchangeCode(code: string): Promise<AuthTokens> {
        const response = await fetch(
            `${authApiUrl}/api/auth/callback?app=${ersAppId}&code=${encodeURIComponent(code)}`,
            { method: 'POST' }
        )
        if (!response.ok) {
            // A 403 here specifically means the user authenticated with Azure AD
            // successfully but isn't assigned to the ERS app in the auth portal --
            // distinct from a bad/expired code, worth surfacing clearly.
            if (response.status === 403) {
                throw new Error('Your account does not have access to ERS admin. Contact an ERS admin to be added.')
            }
            throw await parseAuthError(response, 'Login failed')
        }
        return response.json() as Promise<AuthTokens>
    },

    /** Exchanges a refresh token for a new access token. */
    async refresh(refreshToken: string): Promise<{ access_token: string }> {
        const response = await fetch(`${authApiUrl}/api/auth/refresh?app=${ersAppId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ refresh_token: refreshToken }),
        })
        if (!response.ok) throw await parseAuthError(response, 'Session refresh failed')
        return response.json() as Promise<{ access_token: string }>
    },
}